import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from '@praktio/common-ui';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../ducks/store';
import { getDateString, getLocalTime } from '../../../../utils/functions';
import { logout, updateMaintenanceNotified } from '../../../../ducks/auth';

import './MaintenanceNoticeModal.scss';
import {
  LogoutTypes,
  Minute,
  ModalTypes,
  Second,
  Sixty,
  Thirty,
  ThirtySeconds,
} from '../../../../utils/constants';

interface IProps {
  show: boolean;
  onHide: () => void;
}

const getTimeSpan = (to: Date) => {
  const toTime = new Date(to).getTime();
  const nowTime = new Date().getTime();
  const difference = toTime - nowTime;

  return difference < 0 ? 0 : difference;
};

const getInitialCountdown = (to: Date) => {
  const difference = getTimeSpan(to);
  if (difference > ThirtySeconds) {
    return Math.ceil(difference / Minute);
  }

  return Math.ceil(difference / Second);
};

const getTimeoutMs = (to: Date) => {
  const difference = getTimeSpan(to);
  const timerMs = difference <= Minute ? Second : (Sixty - new Date().getSeconds()) * Second;
  return timerMs;
};

const MaintenanceNoticeModal = ({ show = false, onHide }: IProps) => {
  const dispatch = useDispatch();
  const timerId = useRef<NodeJS.Timeout | undefined>();
  const loggingOut = useRef<boolean>(false);
  const prevModalType = useRef<ValueOf<IModalTypes> | undefined>();
  const { maintenance, modalType } = useSelector((state: RootState) => {
    const { maintenance } = state.platformData;
    const { showType } = state.modal;
    return { maintenance, modalType: showType };
  });

  const [countdown, setCountdown] = useState(
    maintenance?.start ? getInitialCountdown(maintenance.start) : Thirty,
  );
  const prevShow = useRef(show);

  const clearTimeoutAndId = () => {
    clearTimeout(timerId.current);
    timerId.current = undefined;
  };

  useEffect(() => {
    if (
      show &&
      !!maintenance?.start &&
      (modalType === ModalTypes.Maintenance || modalType === ModalTypes.MaintenanceCountdown)
    ) {
      // If the modal was previously hidden, reset the countdown
      if (!prevShow.current) {
        setCountdown(getInitialCountdown(maintenance.start));
      } else if (
        modalType === ModalTypes.MaintenanceCountdown &&
        prevModalType.current !== modalType
      ) {
        setCountdown(Thirty);
      } else {
        const timeoutCallback = () => {
          clearTimeoutAndId();
          if (!loggingOut.current && !!maintenance?.start) {
            const difference = getTimeSpan(maintenance.start);
            // If we're past the date or countdown is 0, stop counting
            if (difference < 0 || countdown <= 0) {
              loggingOut.current = true;
              dispatch(logout(LogoutTypes.Maintenance));
              onHide();
            }
            // Otherwise, if the difference is:
            // 1. over thirty seconds and countdown is > 1
            // 2. OR under thirty seconds
            // subtract from the countdown
            else if (
              (difference > ThirtySeconds && countdown > 1) ||
              difference < ThirtySeconds - Second
            ) {
              setCountdown(countdown - 1);
            }
            // Otherwise, if difference is:
            // Between 29 and 30 seconds, set the countdown to 30
            else if (difference <= ThirtySeconds && difference > ThirtySeconds - Second) {
              setCountdown(Thirty);
            }
          }
        };

        // Only set a timer if one isn't set already
        const timeoutMs = getTimeoutMs(maintenance.start);
        if (!timerId.current) {
          timerId.current = setTimeout(timeoutCallback, timeoutMs);
        }
      }
    } else if (timerId.current) {
      clearTimeoutAndId();
    }

    if (prevShow.current !== show) {
      prevShow.current = show;
    }

    if (prevModalType.current !== modalType) {
      prevModalType.current = modalType;
    }
  }, [show, modalType, maintenance, countdown, dispatch, onHide]);

  const under30 = !!maintenance?.start ? getTimeSpan(maintenance.start) <= ThirtySeconds : false;
  const renderCountdown = () => {
    if (under30) {
      return (
        <>
          You'll be logged out in{' '}
          <span className="timer">
            {countdown} second{countdown !== 1 ? 's' : ''}
          </span>
          .
        </>
      );
    }

    return (
      <>
        Wrap up whatever you're working on—you'll be logged out in{' '}
        <span className="timer">
          {countdown} minute{countdown !== 1 ? 's' : ''}
        </span>
        .
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        dispatch(updateMaintenanceNotified(true));
        onHide();
      }}
      onSave={() => {
        if (under30) {
          clearTimeoutAndId();
          dispatch(logout(LogoutTypes.Normal));
        } else {
          dispatch(updateMaintenanceNotified(true));
        }

        onHide();
      }}
      title={
        (
          <div className="header-text">
            <span className="material-icons">warning</span> Scheduled maintenance is starting soon.
          </div>
        ) as any
      }
      saveText={under30 ? 'LOG OUT' : 'STAY LOGGED IN'}
      className="MaintenanceNoticeModal"
      footerAdditions={
        under30 ? undefined : (
          <Button
            className="secondary"
            onClick={() => {
              clearTimeoutAndId();
              dispatch(logout(LogoutTypes.Normal));
            }}
          >
            LOG OUT
          </Button>
        )
      }
      notDraggable
    >
      <div className="prompt">{renderCountdown()} Don't worry, we'll save your progress.</div>
      <div className="completion">
        Downtime Expected Until: {getDateString(maintenance?.start)} at{' '}
        {getLocalTime(maintenance?.start, maintenance?.duration)}
      </div>
    </Modal>
  );
};

export default MaintenanceNoticeModal;
